<template>
  <slot name="title" :contacts-count="paginatorInfo?.total" :refetch="refetch"></slot>

  <WdsTabs
    v-if="shouldDisplayToggle"
    :active-tab-key="toggledOption"
    @update:active-tab-key="handleToggle"
  >
    <WdsTab :title="$t('Company contacts')" :tab-key="TrustedContactOrigin.COMPANY_CONTACTS" />
    <WdsTab :title="$t('Group contacts')" :tab-key="TrustedContactOrigin.ORGANISATION_CONTACTS" />
  </WdsTabs>

  <div v-if="trustedContactsLoadingError" class="tw-mt-9">
    <ContentLoadingErrorNotice />
  </div>
  <template v-else>
    <div class="card card-white" style="height: calc(100% - 50px)">
      <TrustedContactsList
        :additional-columns="columns"
        :entries="entries"
        :loading="loading"
        :is-error="trustedContactsLoadingError"
        :paginator-info="paginatorInfo"
        :multi-select="multiSelect"
        :pre-selected-rows="preSelectedRows"
        :for-organisation="forOrganisation"
        :show-worker-skills="showWorkerSkills"
        @row-click="emit('row-click', $event)"
        @rows-selected="emit('rows-selected', $event)"
        @search="search"
      >
        <template v-for="name in Object.keys($slots).filter(filterSlots)" #[name]="slotProps">
          <slot :name="name" :refetch="refetch" v-bind="slotProps || {}"></slot>
        </template>
        <template #control-bar>
          <TrustedContactsControlBar
            :default-page-length="DEFAULT_PAGE_LENGTH"
            :for-organisation="forOrganisation"
            :divisions="divisions"
            :unfiltered-divisions="unfilteredDivisions"
            :worker-panel-active="props.workerPanelActive"
            :staffing-agency-manages-workers="staffingAgencyManagesWorker"
            :show-worker-skills="showWorkerSkills"
            @filter-drawer-toggled="$emit('filter-drawer-toggled', $event)"
            @search="searchInput"
            @sort="sort"
            @update-filters="updateFilters"
            @update:show-worker-skills="updateShowWorkerSkills"
          />
        </template>
      </TrustedContactsList>
      <!-- Use this slot as a provider, allowing you to perform actions needing to refresh after-->
      <slot name="actions" :refetch="refetch"></slot>
    </div>
  </template>
</template>

<script setup lang="ts">
import { computed, ref, Ref } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import { useDebounceFn } from '@vueuse/core'
import { WdsTabs, WdsTab } from '@worksome/wds-components'
import { $t } from '@/composables/translation'
import SilentNetworkError from '@/utils/notification/SilentNetworkError'
import ContentLoadingErrorNotice from '@/components/ContentLoadingErrorNotice.vue'
import TrustedContactsControlBar from '@/components/Contacts/CompanyFreelancer/PageContacts/TrustedContactsControlBar.vue'
import TrustedContactsList from '@/components/Contacts/CompanyFreelancer/PageContacts/TrustedContactsList.vue'

import {
  OrganisationTrustedContactsList,
  TrustedContactsList as CompanyTrustedContactsList,
} from '@/components/Contacts/CompanyFreelancer/queries/TrustedContacts'
import { CompanyQuery } from '@/components/Contacts/CompanyFreelancer/queries/Company'
import useTrustedContactsList, {
  TrustedContactListVariables,
} from '@/components/Contacts/CompanyFreelancer/composables/trustedContactsList'
import { TrustedContact } from '@/components/Job/hiring-types'
import { TrustedContactOrigin } from '@/components/Contacts/CompanyFreelancer/types'
import { SortOrder, TrustedContactOrderByColumn } from '@/gql/graphql'

interface Props {
  multiSelect?: boolean
  preSelectedTrustedContacts?: TrustedContact[]
  additionalColumns?: object[]
  workerPanelActive?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  multiSelect: false,
  preSelectedTrustedContacts: () => [],
  additionalColumns: () => [],
  workerPanelActive: false,
})

const emit = defineEmits([
  'rows-selected',
  'row-click',
  'search',
  'tab-toggled',
  'filter-drawer-toggled',
])

const trustedContactsLoadingError = ref(false)

const DEFAULT_PAGE_LENGTH = 50

const organisationContactsColumns = [
  {
    key: 'company',
    title: $t('Originating company'),
    class: 'vmiddle',
  },
]

const toggledOption = ref(TrustedContactOrigin.COMPANY_CONTACTS)

const { result: companyQueryResult } = useQuery(
  CompanyQuery,
  { companyId: currentAccount.globalId },
  {
    clientId: 'publicApiCustomFields',
  },
)

const staffingAgencyManagesWorker = computed(
  () => companyQueryResult.value?.company?.settings?.staffingAgencyManagesWorker ?? false,
)

const unfilteredDivisions = computed(
  () =>
    companyQueryResult.value?.company?.organisations?.data
      ?.map((organisation) => organisation.divisions?.data.map((division) => division.id))
      .flat() ?? [],
)

const divisions = computed(() =>
  unfilteredDivisions.value.filter((division) => division !== currentAccount.globalId),
)

const forOrganisation = computed(
  () => toggledOption.value === TrustedContactOrigin.ORGANISATION_CONTACTS,
)

// Determine query variables & table columns based on if the company or organization contacts are selected
const statuses = computed(() => (!forOrganisation.value ? [] : ['ACTIVE']))
const columns = computed(() => [
  ...(forOrganisation.value ? organisationContactsColumns : []),
  ...props.additionalColumns,
])

const query = computed(() =>
  !forOrganisation.value ? CompanyTrustedContactsList : OrganisationTrustedContactsList,
)

const queryVariables: Ref<TrustedContactListVariables> = ref({
  accounts: [currentAccount.globalId],
  allAccounts: [currentAccount.globalId],
  statuses: statuses.value,
  page: 1,
  perPage: DEFAULT_PAGE_LENGTH,
  search: null,
  origin: null,
  orderBy: [
    {
      column: TrustedContactOrderByColumn.WorkerName,
      order: SortOrder.Asc,
    },
  ],
  markets: [],
  invitedByUsers: [],
  skills: [],
  companyId: currentAccount.globalId,
  hireHistory: [],
  hireStatus: [],
  staffingAgencyStatus: null,
  customFields: [],
})

const {
  trustedContacts: entries,
  rawResult,
  loading,
  refetch,
  onError: onTrustedContactsLoadingError,
} = useTrustedContactsList(query, queryVariables)

onTrustedContactsLoadingError((error) => {
  trustedContactsLoadingError.value = true

  new SilentNetworkError({
    ex: error,
  }).dispatch()
})

const preSelectedRows = computed(() =>
  entries.value.filter((entry) =>
    props.preSelectedTrustedContacts.find(
      (trustedContact) => trustedContact.workerId === entry.workerId,
    ),
  ),
)

const shouldDisplayToggle = computed(
  () => companyQueryResult.value?.company?.organisations?.paginatorInfo?.count > 0,
)

const paginatorInfo = computed(() => rawResult.value?.trustedContacts?.paginatorInfo)

const updateFilters = (filters) => {
  queryVariables.value = {
    ...queryVariables.value,
    ...filters,
    // Reset page when filtering so that resulting records will display
    page: 0,
  }
}

const searchInput = useDebounceFn((search) => {
  queryVariables.value = {
    ...queryVariables.value,
    // Reset page when searching so that resulting records will display
    page: 0,
    search,
  }
}, 600)

const search = (values) => {
  queryVariables.value = {
    ...queryVariables.value,
    page: values.page + 1,
    perPage: values.perPage,
  }
}

const sort = (orderBy) => {
  queryVariables.value = {
    ...queryVariables.value,
    orderBy,
  }
}

const updateAccounts = (optionKey, useUnfilteredDivisions = false) => {
  const divisionsToUse = useUnfilteredDivisions ? unfilteredDivisions.value : divisions.value

  queryVariables.value[useUnfilteredDivisions ? 'allAccounts' : 'accounts'] =
    optionKey === TrustedContactOrigin.ORGANISATION_CONTACTS
      ? divisionsToUse
      : [currentAccount.globalId]
}

const handleToggle = (optionKey) => {
  toggledOption.value = optionKey
  queryVariables.value.page = 0
  updateAccounts(optionKey)
  updateAccounts(optionKey, true)
  emit('tab-toggled', optionKey)
}

const filterSlots = (slotName: string) => !['control-bar'].includes(slotName)

const showWorkerSkills = ref(true)

function updateShowWorkerSkills(value) {
  showWorkerSkills.value = value
}
</script>

<style lang="scss" scoped>
.direct-hire-button {
  @apply tw-mr-4;
}
</style>
